import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { SP, PANEL_RADIUS, CONTENT_WIDTH } from '../../theme/tokens';

/**
 * RouteFallback — the placeholder a lazy route shows while its chunk arrives.
 *
 * @description
 *   Fifteen heavy pages load through `React.lazy` (App.js). While a chunk is in
 *   flight the router has nothing to render, so this stands in. It is a SKELETON,
 *   not a spinner and not a line of text: `anti-patterns.md` requires the
 *   placeholder to be shaped like the content that replaces it.
 *
 *   🔴 **Dass die Seite deshalb nicht springt, ist FALSCH** — korrigiert
 *   2026-09-22, nachdem ein UX-Gate es nachgemessen hat. Die Form kauft die
 *   richtige ART Inhalt, keine stabile Geometrie:
 *     - zwölf der sechzehn lazy Seiten liegen bei 1280 (elf über `PageContainer` —
 *       `maxWidth` in `PageContainer.jsx` —, die zwölfte `/design` über eine rohe `Box`
 *       mit `CONTENT_WIDTH.reading`, `DesignCatalog.jsx:104`), polstern 24, unter
 *       600 px 12 (`PAGE_GUTTER`), dieser Platzhalter bei 1280 und 32 → 0 px Breite und
 *       8 px (unter 600 px 20 px) Innenabstand
 *     - die Lieferantenseite polstert unter 600 px mit 16 gegen 32 → 16 px
 *     - die Hoehe weicht in JEDEM Fall ab (~506 px hier gegen eine volle Tabelle)
 *   Die Zahlen sind aus den CSS-Werten gerechnet, nicht an einem Rendering
 *   abgelesen. Wer sie braucht, rechnet sie nach.
 *
 *   It renders INSIDE the app frame when the INNER `<Suspense>` (around the
 *   `<Outlet/>` in `SidebarAndHeaderBar`) suspends — but the OUTER one in `App.js`
 *   and `VendorReportView` render it outside the frame, full width, on first load.
 *
 * @useWhen
 *   - As the `fallback` of a `<Suspense>` around routed content
 *   - As the WHOLE-page first-load state of a lazy page that has no skeleton of
 *     its own (`VendorReportView`) — the route fallback hands over to the same
 *     shape instead of a spinner, so skeleton → spinner → page becomes
 *     skeleton → page (Regel-16-Gate B3, 2026-10-03)
 *
 * @dontUseFor
 *   - Data loading inside a page that is already showing — use that page's own
 *     section skeleton (`Skeletons/ProductsTableSkeleton`, …)
 *   - Anything needing a message — this is deliberately wordless
 *
 * @related
 *   - ErrorBoundary — the other half of the pair: this covers "still loading",
 *     that covers "failed"
 *
 * @reuseHints
 *   - Any future lazy boundary (a modal body, a tab panel) wants the same shape
 *
 * @props
 *   None.
 */
export default function RouteFallback() {
  return (
    <Box
      data-testid="route-fallback"
      // 🔴 G0b.5 (2026-10-13), NW5.7:809: „jedes Skelett und jeder Platzhalter
      // traegt das Muster seiner Seite." Der Platzhalter fuehrte bis hierher das
      // Literal 1200 — eine Abschrift des oeffentlichen Lieferantenberichts
      // (`VendorReportView.jsx:2074`, der nach F3 **bewusst** bei 1200 bleibt).
      // Ein zweites Mass fuer denselben Platzhalter war eine zweite Wahrheit;
      // jetzt liest er `CONTENT_WIDTH.reading` (1280) — **ein Muster, ein
      // Platzhalter** (`inhaltsbreite.md` §5.3:724). Die Ausrichtung bleibt
      // (`mx: 'auto'`), die Groessenordnung auch.
      sx={{ p: SP.page, maxWidth: CONTENT_WIDTH.reading, mx: 'auto' }}
      // 🔴 Was diese zwei Attribute leisten — und was NICHT. Sie sind die beste
      // Verfuegung, die ein Fallback treffen kann, und sie sind schwach:
      // `role="status"` kommt hier MIT seinem Inhalt in den Baum, und genau diese
      // Bauart dokumentiert das Repo an drei Stellen als oft ungehoert —
      // `PODetail.jsx:3545`, `PdfInlineFrame.jsx:21` und `:347`, alle aelter als
      // diese Datei. Der tragfaehige Weg waere eine DAUERHAFT eingehaengte,
      // visuell verborgene Region; ein Fallback, der genau dann existiert, wenn
      // geladen wird, kann das nicht sein. Was bleibt, ist der Name.
      //
      // ⚠️ `aria-busy="true"` stand hier bis 2026-09-22 und ist ENTFERNT: hart
      // verdrahtet, nie zurueckgesetzt — das Element wird ausgehaengt. Eine
      // Live-Region, die dauerhaft „busy" ist, schweigt. Das Repo macht es an
      // der Stelle, an der es wirkt, anders: `PdfInlineFrame.jsx:453` setzt
      // `aria-busy={state === PDF_PROBING}` und loest es wieder.
      role="status"
      aria-label="Loading page"
    >
      <Skeleton variant="text" width="38%" height={34} />
      <Skeleton variant="text" width="62%" height={20} sx={{ mt: SP.tight }} />
      <Skeleton variant="rectangular" height={120} sx={{ mt: SP.md, borderRadius: PANEL_RADIUS }} />
      <Skeleton variant="rectangular" height={220} sx={{ mt: SP.card, borderRadius: PANEL_RADIUS }} />
    </Box>
  );
}
